import type { ComponentChildren } from 'preact';
import { useLayoutEffect, useRef, useState } from 'preact/hooks';

// A row that scrolls sideways says so (consistency pass R9 M2, M3, M5; Kristi Nov 2, answer 2a):
// while there's more past an edge, the last 52 px fade into the surface with a teal › (and ‹ at
// the start once you've scrolled). Tapping an arrow moves most of a screen's width. At the end the
// fade goes. On opening and whenever `chosen` changes, the chosen item (a tab, a switch option)
// scrolls fully into view. Rows that fit show nothing extra, so on a laptop this is invisible.
// Use it for tab rows (TabRow does), long view switches (Segmented `edge`) and chip rows too
// long to wrap in two lines (ChipRow `scroll`).

const FADE = 52;

export function EdgeScroll({
  children,
  label,
  chosen,
  surface = 'page',
  class: cls = '',
}: {
  children: ComponentChildren;
  /** What scrolls, for the arrows' names ("Scroll Review's lists right"). */
  label?: string;
  /** Changes when the choice changes; the chosen item ([aria-selected], [aria-checked], .on) scrolls into view. */
  chosen?: unknown;
  /** The surface under the row, so the fade takes its colour: the page, a panel or a card. */
  surface?: 'page' | 'panel' | 'card' | 'sheet';
  class?: string;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [ends, setEnds] = useState({ left: false, right: false });

  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const check = () => {
      const left = el.scrollLeft > 1;
      const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
      setEnds((e) => (e.left === left && e.right === right ? e : { left, right }));
    };
    check();
    el.addEventListener('scroll', check, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(check) : null;
    ro?.observe(el);
    if (el.firstElementChild) ro?.observe(el.firstElementChild);
    return () => {
      el.removeEventListener('scroll', check);
      ro?.disconnect();
    };
  }, []);

  // The chosen item sits fully on screen, clear of the fades (M3).
  const first = useRef(true);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const item = el.querySelector<HTMLElement>('[aria-selected="true"], [aria-checked="true"], .on');
    const instant = first.current;
    first.current = false;
    if (!item || el.scrollWidth <= el.clientWidth) return;
    const a = item.getBoundingClientRect();
    const b = el.getBoundingClientRect();
    const start = a.left - b.left + el.scrollLeft;
    const end = start + a.width;
    let to: number | null = null;
    if (start < el.scrollLeft + (el.scrollLeft > 1 ? FADE : 0)) to = Math.max(0, start - FADE);
    else if (end > el.scrollLeft + el.clientWidth - FADE) to = end - el.clientWidth + FADE;
    if (to === null) return;
    if (instant || typeof el.scrollTo !== 'function') el.scrollLeft = to;
    else el.scrollTo({ left: to, behavior: 'smooth' });
  }, [chosen]);

  const move = (dir: 1 | -1) => {
    const el = box.current;
    if (!el) return;
    const by = Math.max(80, el.clientWidth - 2 * FADE) * dir;
    if (typeof el.scrollBy === 'function') el.scrollBy({ left: by, behavior: 'smooth' });
    else el.scrollLeft += by;
  };

  return (
    <div class={`edge-scroll edge-on-${surface}${ends.left ? ' more-left' : ''}${ends.right ? ' more-right' : ''}${cls ? ` ${cls}` : ''}`}>
      <div class="edge-track" ref={box}>
        {children}
      </div>
      {ends.left ? (
        <span class="edge-fade left">
          <button type="button" class="edge-arrow" tabIndex={-1} aria-label={label ? `Scroll ${label} left` : 'Scroll left'} onClick={() => move(-1)}>
            ‹
          </button>
        </span>
      ) : null}
      {ends.right ? (
        <span class="edge-fade right">
          <button type="button" class="edge-arrow" tabIndex={-1} aria-label={label ? `Scroll ${label} right` : 'Scroll right'} onClick={() => move(1)}>
            ›
          </button>
        </span>
      ) : null}
    </div>
  );
}
